import React, { useEffect, useRef } from 'react';
import ConsentGuard from './ConsentGuard';

declare global {
  interface Window {
    OPWIDGET: {
      base_url: string;
      api_key: string;
      options: {
        primarycolor: string;
        secondarycolor: string;
        backgroundcolor: string;
        mainfontsize: string;
      };
    };
  }
}

const PetitionWidgetContent: React.FC = () => {
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const scriptLoaded = useRef(false);

  useEffect(() => {
    // Prevent double loading in strict mode
    if (scriptLoaded.current) return;
    scriptLoaded.current = true;

    // Set the global OPWIDGET configuration
    window.OPWIDGET = {
      base_url: "https://www.openpetition.de",
      api_key: "8f58a6fbb49e19718b76632276f5cd3130119e9f75d43b326de8ff414eba766c",
      options: {
        primarycolor: "#2563eb", // Passend zur Vereinsfarbe (Blue-600)
        secondarycolor: "#1e3a8a", // Blue-900
        backgroundcolor: "#f8fafc", // Slate-50
        mainfontsize: "16px"
      }
    };

    // Load the OpenPetition widget script
    const script = document.createElement('script');
    script.src = 'https://www.openpetition.de/javascript/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup on unmount
      const existingScript = document.querySelector('script[src="https://www.openpetition.de/javascript/widget.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div 
      ref={widgetContainerRef}
      id="op_widget_container_8f58a6fbb49e19718b76632276f5cd3130119e9f75d43b326de8ff414eba766c"
      className="petition-widget-container bg-white rounded-xl shadow-lg p-4 border border-gray-100"
    />
  );
};

const PetitionWidget: React.FC = () => {
  return (
    <section className="bg-gradient-to-b from-primary-mist to-white border-b border-gray-200">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header - passend zum Website-Design */}
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-2 text-base text-red-600 font-semibold tracking-wide uppercase mb-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Wichtige Petition
          </span>
          <h2 className="text-2xl sm:text-3xl leading-8 font-extrabold tracking-tight text-gray-900">
            Rettet unseren Flugplatz Bad Langensalza!
          </h2>
          <p className="mt-3 max-w-2xl text-lg text-gray-500 mx-auto">
            98 Jahre Thüringer Flugtradition in Gefahr – Jetzt unterschreiben und helfen!
          </p>
        </div>
        
        {/* Widget Container with Guard */}
        <ConsentGuard title="openPetition Widget">
          <PetitionWidgetContent />
        </ConsentGuard>
        
        {/* Noscript Fallback */}
        <noscript>
          <div className="text-center p-6 bg-primary-mist rounded-xl border border-sky-200 mt-4">
            <p className="text-gray-700 mb-3">
              Bitte aktivieren Sie JavaScript, um das Petitions-Formular zu sehen.
            </p>
            <a 
              href="https://www.openpetition.de/petition/online/rettet-unseren-flugplatz-bad-langensalza-98-jahre-thueringer-flugtradition-in-gefahr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-colors duration-200"
            >
              Zur Petition auf openPetition.de
              <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          </div>
        </noscript>
      </div>
    </section>
  );
};

export default PetitionWidget;
